import React, { createContext, useContext, useEffect, useState } from "react";
import { NewsData, NEWS_API_ENDPOINTS, defaultNewsData, DefaultImageList, defaultImageListData } from "./constants";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../utils/axiosInstance";
import { message } from "antd";

interface NewsContextState {
	newsData: NewsData;
	defaultImageList: DefaultImageList[];
	loadingGetNewsData?: boolean;
	loadingAddNewsData: boolean;
	loading: boolean;
	activeSameImages: boolean;
	loadingEditNewsData: boolean;
	setNewsData: React.Dispatch<React.SetStateAction<NewsData>>;
	setDefaultImageList: React.Dispatch<React.SetStateAction<DefaultImageList[]>>;
	addNews: () => void;
	updateNews: () => void;
	clearNewsData: () => void;
	getNewsDetails: (id: string) => void;
	setActiveSameImages: React.Dispatch<React.SetStateAction<boolean>>;
}

interface NewsProviderProps {
	children: React.ReactNode;
}

const NewsContext = createContext<NewsContextState | undefined>(
	undefined
);

export const useNewsContext = () => {
	const context = useContext(NewsContext);
	if (!context) {
		throw new Error(
			"useNewsContext must be used within an NewsContextProvider"
		);
	}
	return context;
};

function isFileObject(value: any) {
	return value instanceof File;
}

export const NewsContextProvider: React.FC<NewsProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [newsData, setNewsData] = useState<NewsData>(defaultNewsData);
	const [defaultImageList, setDefaultImageList] = useState<DefaultImageList[]>(defaultImageListData);
	const [activeSameImages, setActiveSameImages] = useState<boolean>(false);

	const [loadingAddNewsData, setLoadingAddNewsData] = useState(false);
	const [loadingEditNewsData, setLoadingEditNewsData] = useState(false);
	const [loadingGetNewsData, setLoadingGetNewsData] = useState(false);
	const [errorAddNewsData, setErrorAddNewsData] = useState<any>(undefined);

	const loading = loadingAddNewsData
	const error = errorAddNewsData

	const isNewsDataValid = (data: NewsData) => {
		if (!data.releaseDate || data.isVisible === undefined || data.displayOrder === undefined
			|| !data.newsDetail || data.newsDetail.length === 0) {
			return false;
		}

		for (const detail of data.newsDetail) {
			if (!detail.title || !detail.shorText || !detail.text || !detail.culture ||
				!detail.imageUrl || !detail.smallImageUrl || !detail.mobileImageUrl
				|| !detail.mobileSmallImageUrl) {
				return false;
			}
		}
		return true;
	}

	const clearNewsData = () => {
		setNewsData(defaultNewsData);
		setDefaultImageList(defaultImageListData);
	}

	const addNews = async () => {
		setLoadingAddNewsData(true);

		if (newsData.activeSameImages) {
			const trTrDetail = newsData.newsDetail.find(detail => detail.culture === "tr-TR");
			const enUsDetail = newsData.newsDetail.find(detail => detail.culture === "en-US");

			if (trTrDetail && enUsDetail) {
				if (trTrDetail.imageUrl) enUsDetail.imageUrl = trTrDetail.imageUrl;
				if (trTrDetail.smallImageUrl) enUsDetail.smallImageUrl = trTrDetail.smallImageUrl;
				if (trTrDetail.mobileImageUrl) enUsDetail.mobileImageUrl = trTrDetail.mobileImageUrl;
				if (trTrDetail.mobileSmallImageUrl) enUsDetail.mobileSmallImageUrl = trTrDetail.mobileSmallImageUrl;
			}
		}

		if (!isNewsDataValid(newsData)) {
			message.info("Lütfen tüm alanları doldurun.");
			setLoadingAddNewsData(false);
			return;
		}

		const formData = new FormData();
		formData.append('releaseDate', newsData.releaseDate);
		formData.append('isVisible', newsData.isVisible.toString());
		formData.append('displayOrder', newsData.displayOrder?.toString() ?? '');

		newsData.newsDetail.forEach((detail, index) => {
			formData.append(`newsDetail[${index}].title`, detail.title);
			formData.append(`newsDetail[${index}].shorText`, detail.shorText);
			const styledText = detail.text.replace(/<p>/g, `<p style="font-family: 'Roboto';font-style: normal;font-weight: 500;font-size: 16px;line-height: 29px;color: #e9e9e9;">`);
			formData.append(`newsDetail[${index}].text`, styledText);
			formData.append(`newsDetail[${index}].culture`, detail.culture);

			if (detail.imageUrl) {
				formData.append(`newsDetail[${index}].imageUrl`, detail.imageUrl);
			}
			if (detail.smallImageUrl) {
				formData.append(`newsDetail[${index}].smallImageUrl`, detail.smallImageUrl);
			}
			if (detail.mobileImageUrl) {
				formData.append(`newsDetail[${index}].mobileImageUrl`, detail.mobileImageUrl);
			}
			if (detail.mobileSmallImageUrl) {
				formData.append(`newsDetail[${index}].mobileSmallImageUrl`, detail.mobileSmallImageUrl);
			}
		});

		try {
			const response = await axiosInstance.post(`${NEWS_API_ENDPOINTS.ADD_NEWS}`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});

			setLoadingAddNewsData(false);

			if (response.data && response.data.result.isSuccessful) {
				message.success("Haber başarıyla eklendi.");
				setNewsData(defaultNewsData);
				navigate("/news");
			} else {
				message.error(response.data.result.error.message);
			}
		} catch (error) {
			console.error(error);
			setErrorAddNewsData(error);
			setLoadingAddNewsData(false);
		}
	};

	const updateNews = async () => {
		setLoadingEditNewsData(true);

		if (newsData.activeSameImages) {
			const trTrDetail = newsData.newsDetail.find(detail => detail.culture === "tr-TR");
			const enUsDetail = newsData.newsDetail.find(detail => detail.culture === "en-US");

			if (trTrDetail && enUsDetail) {
				if (trTrDetail.imageUrl) enUsDetail.imageUrl = trTrDetail.imageUrl;
				if (trTrDetail.smallImageUrl) enUsDetail.smallImageUrl = trTrDetail.smallImageUrl;
				if (trTrDetail.mobileImageUrl) enUsDetail.mobileImageUrl = trTrDetail.mobileImageUrl;
				if (trTrDetail.mobileSmallImageUrl) enUsDetail.mobileSmallImageUrl = trTrDetail.mobileSmallImageUrl;
			}
		}

		if (!isNewsDataValid(newsData)) {
			message.info("Lütfen tüm alanları doldurun.");
			setLoadingEditNewsData(false);
			return;
		}

		const formData = new FormData();
		formData.append('releaseDate', newsData.releaseDate);
		formData.append('isVisible', newsData.isVisible.toString());
		formData.append('displayOrder', newsData.displayOrder?.toString() ?? '');
		formData.append('newsId', newsData.newsId?.toString() ?? '');

		newsData.newsDetail.forEach((detail, index) => {
			formData.append(`newsDetail[${index}].title`, detail.title);
			formData.append(`newsDetail[${index}].shorText`, detail.shorText);
			let styledText = detail.text.replace(/<p>/g, `<p style="font-family: 'Roboto';font-style: normal;font-weight: 500;font-size: 16px;line-height: 29px;color: #e9e9e9;">`);
			styledText = styledText.replace(/<a /g, `<a style="color: #e9e9e9;" `);
			formData.append(`newsDetail[${index}].text`, styledText);
			formData.append(`newsDetail[${index}].culture`, detail.culture);

			if (!isFileObject(detail.imageUrl)) {
				detail.imageUrl = null;
			}
			if (!isFileObject(detail.smallImageUrl)) {
				detail.smallImageUrl = null;
			}
			if (!isFileObject(detail.mobileImageUrl)) {
				detail.mobileImageUrl = null;
			}
			if (!isFileObject(detail.mobileSmallImageUrl)) {
				detail.mobileSmallImageUrl = null;
			}

			if (detail.imageUrl) {
				formData.append(`newsDetail[${index}].imageUrl`, detail.imageUrl);
			}
			if (detail.smallImageUrl) {
				formData.append(`newsDetail[${index}].smallImageUrl`, detail.smallImageUrl);
			}
			if (detail.mobileImageUrl) {
				formData.append(`newsDetail[${index}].mobileImageUrl`, detail.mobileImageUrl);
			}
			if (detail.mobileSmallImageUrl) {
				formData.append(`newsDetail[${index}].mobileSmallImageUrl`, detail.mobileSmallImageUrl);
			}
		});

		try {
			const response = await axiosInstance.put(`${NEWS_API_ENDPOINTS.UPDATE_NEWS}`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});

			setLoadingEditNewsData(false);

			if (response.data && response.data.result.isSuccessful) {
				message.success("Haber başarıyla güncellendi.");
				setNewsData(defaultNewsData);
				navigate("/news");
			} else {
				message.error(response.data.result.error.message);
			}
		} catch (error) {
			console.error(error);
			setErrorAddNewsData(error);
			setLoadingEditNewsData(false);
		}
	};

	const getNewsDetails = async (id: string) => {
		setLoadingGetNewsData(true);
		try {
			const response = await axiosInstance.get(`${NEWS_API_ENDPOINTS.GET_NEWS_DETAILS}?newsId=${id}`);
			if (response.data && response.data.isSuccessful) {
				setNewsData(response?.data?.result);
				setDefaultImageList(response?.data?.result?.newsDetail)
			}
			setLoadingGetNewsData(false);
		} catch (error) {
			console.error(error);
			setErrorAddNewsData(error);
			setLoadingGetNewsData(false);
		}
	};

	const value: NewsContextState = {
		newsData,
		defaultImageList,
		loadingGetNewsData,
		loadingAddNewsData,
		loadingEditNewsData,
		loading,
		activeSameImages,
		setNewsData,
		setDefaultImageList,
		addNews,
		clearNewsData,
		updateNews,
		getNewsDetails,
		setActiveSameImages,
	};

	return (
		<NewsContext.Provider value={value}>
			{children}
		</NewsContext.Provider>
	);
};
